/* shared/layout — app shell chrome + layout shells.
   Combines shell.jsx (Sidebar, Topbar, ModuleHeader + sub-components) and
   layouts.jsx (AppLayout, SettingsWorkspaceLayout, AuthLayout, useLayoutCtx).
   window.* globals replaced with ESM imports/exports.

   NOTE (Phase 1 dependency): ProfileMenu still reads window.__nx_auth /
   window.__nx_settings defensively. Those globals are owned by the auth tier,
   which is converted in a later phase. Reads are guarded + fall back to a demo
   profile, so behavior is preserved until auth is wired via context. */
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { NexusIcon } from '../ui/NexusIcon';
import { Avatar, Btn, IconBtn, Kbd } from '../ui';
import { useToast } from '../feedback';
import { NexusTheme } from '../theme';
import { useRbac } from '../../rbac/RbacContext';
import { useStore } from '../state';
import { hasFeature } from '../../platform/featureRegistry.js';
import { useWorkspaceModules, useWorkspace } from '../../workspace/WorkspaceRuntimeContext';
import { MODULE_ID_TO_BACKEND } from '../modules.js';
import * as coApi from '../../api/custom-objects.api.js';

/* ── Layout context ──────────────────────────────────────────────────── */
const LayoutCtx = createContext({ collapsed: false, setCollapsed: () => {} });
export const useLayoutCtx = () => useContext(LayoutCtx);

/* ═══════════════════════════ Sidebar ═══════════════════════════ */
/* Platform/admin nav is now role-driven and passed in via the `adminNav` prop
   (computed by App from RBAC). Home is always rendered; admin items conditional. */
/* FOUNDATION-004B: each item carries its canonical leaf feature key so the sidebar
   can gate by both workspace-level enabledModules AND org-level plan entitlements.
   Empty planFeatureKeys → no feature restriction (graceful fallback). */
const SIDEBAR_WORKSPACE = [
  { id: 'people',    label: 'People',    icon: 'User',        featureKey: 'crm.people.view' },
  { id: 'companies', label: 'Companies', icon: 'Building',    featureKey: 'crm.companies.view' },
  { id: 'tasks',     label: 'Tasks',     icon: 'CheckSquare', featureKey: 'crm.tasks.view' },
  { id: 'notes',     label: 'Notes',     icon: 'StickyNote',  featureKey: 'crm.notes.view' },
];

const SIDEBAR_SALES = [
  { id: 'sales',        label: 'Sales Workspace',       icon: 'Briefcase',    featureKey: 'crm.pipeline.view' },
  { id: 'deals',        label: 'Deals',                 icon: 'Target',       featureKey: 'crm.deals.view' },
  { id: 'pipeline',     label: 'Pipeline',              icon: 'Columns',      featureKey: 'crm.deals.pipeline.manage' },
  { id: 'forecast',     label: 'Forecast',              icon: 'TrendingUp',   featureKey: 'crm.deals.forecast' },
  { id: 'intelligence', label: 'Pipeline Intelligence', icon: 'Activity',     featureKey: 'crm.pipeline.view' },
  { id: 'quotes',       label: 'Quotes',                icon: 'FileText',     featureKey: 'crm.deals.view' },
  { id: 'products',     label: 'Products',              icon: 'Package',      featureKey: 'crm.deals.view' },
  { id: 'price-books',  label: 'Price Books',           icon: 'BookOpen',     featureKey: 'crm.deals.view' },
  { id: 'approvals',    label: 'Approvals',             icon: 'ClipboardList', featureKey: 'crm.deals.view' },
  { id: 'goals',        label: 'Goals',                 icon: 'Award',        featureKey: 'crm.pipeline.view' },
  { id: 'territory',    label: 'Territory',             icon: 'MapPin',       featureKey: 'crm.pipeline.view' },
];

const SIDEBAR_ENGAGE = [
  { id: 'emails',        label: 'Email',            icon: 'Mail',          featureKey: 'channels.email.use' },
  { id: 'meetings',      label: 'Meetings',         icon: 'Video',         featureKey: 'crm.activities.view' },
  { id: 'activities',    label: 'Activities',       icon: 'Activity',      featureKey: 'crm.activities.view' },
  { id: 'timeline',      label: 'Customer Timeline', icon: 'Clock',        featureKey: 'crm.activities.view' },
  { id: 'collaboration', label: 'Collaboration',    icon: 'MessageSquare', featureKey: 'crm.activities.view' },
  { id: 'notifications', label: 'Notifications',    icon: 'Bell',          featureKey: 'crm.activities.view' },
  { id: 'workflows',     label: 'Workflows',        icon: 'Zap',           featureKey: 'crm.activities.view' },
  { id: 'customer-360',  label: 'Customer 360',     icon: 'Users',         featureKey: 'crm.activities.view' },
];

/* Phase 5+ sidebar sections — feature keys fixed to real plan leaf keys */
const SIDEBAR_AI = [
  { id: 'copilot',           label: 'AI Assistant',     icon: 'Star',      featureKey: 'ai.copilot.use' },
  { id: 'llm-gateway',       label: 'AI Gateway',       icon: 'Cpu',       featureKey: 'ai.copilot.use' },
  { id: 'prompt-templates',  label: 'Prompt Templates', icon: 'FileEdit',  featureKey: 'ai.copilot.use' },
  { id: 'ai-sessions',       label: 'AI Sessions',      icon: 'Database',  featureKey: 'ai.copilot.use' },
  { id: 'ml-scores',         label: 'ML Scores',        icon: 'Gauge',     featureKey: 'ai.copilot.use' },
  { id: 'health-score',      label: 'Health Score',     icon: 'HeartPulse', featureKey: 'ai.copilot.use' },
];
const SIDEBAR_INSIGHTS5 = [
  { id: 'analytics',        label: 'Analytics',        icon: 'BarChart2',      featureKey: 'analytics.crm.basic' },
  { id: 'reports',           label: 'Reports',          icon: 'FileText',       featureKey: 'analytics.crm.basic' },
  { id: 'dashboards',        label: 'Dashboards',       icon: 'LayoutDashboard', featureKey: 'analytics.crm.basic' },
  { id: 'saved-searches',    label: 'Saved Searches',   icon: 'Search',         featureKey: 'analytics.crm.basic' },
  { id: 'revenue-analytics', label: 'Revenue Analytics', icon: 'DollarSign',    featureKey: 'analytics.crm.revenue' },
  { id: 'realtime',          label: 'Live Feed',        icon: 'Radio',          featureKey: 'analytics.crm.basic' },
];
const SIDEBAR_PLATFORM5 = [
  { id: 'forms',          label: 'Forms',           icon: 'ClipboardList', featureKey: 'crm.people.view' },
  { id: 'import-export',  label: 'Import / Export', icon: 'RefreshCw',     featureKey: 'crm.people.view' },
  { id: 'webhooks',       label: 'Webhooks',        icon: 'Webhook',       featureKey: 'developer.webhooks.view' },
  /* CRM-WORKSPACE-003: was 'crm.pipeline.view' for all three — absent from all standard plans.
     integration → org.integrations.view (canonical for integration management, featureRegistry §org.integrations)
     plugins     → developer.oauth-apps.view (OAuth Apps are the extensibility/plugin mechanism, Enterprise+ via parent key)
     jobs        → crm.activities.view (background job monitoring parallel to Automation; all plans) */
  { id: 'integration',    label: 'Integrations',    icon: 'Plug',          featureKey: 'org.integrations.view'    },
  { id: 'plugins',        label: 'Plugins',         icon: 'Layers',        featureKey: 'developer.oauth-apps.view' },
  { id: 'jobs',           label: 'Jobs',            icon: 'HardDrive',     featureKey: 'crm.activities.view'      },
  /* custom-objects schema manager removed from CRM Platform section — definition management
     now lives exclusively in Workspace Console → CRM Configuration → Custom Objects.
     Per-object record shortcuts appear in the dynamic Objects sidebar section below. */
  { id: 'automation',     label: 'Automation',      icon: 'GitBranch',     featureKey: 'crm.activities.view' },
];

/* MODULE_ID_TO_BACKEND imported from ../modules.js — covers all 44 enterprise modules */

/* CO-011c: max dynamic custom-object entries before overflow "+N more" */
const CO_SIDEBAR_MAX = 8;

/* CO-011c: fetches ACTIVE custom object defs for the sidebar; re-fetches on the
   nx-custom-objects-changed DOM event (publish / archive / restore). */
function useActiveCustomObjects(wsId) {
  const [defs, setDefs] = useState([]);
  const fetchDefs = React.useCallback(async () => {
    if (!wsId) { setDefs([]); return; }
    try {
      const result = await coApi.listDefs(wsId);
      setDefs(Array.isArray(result) ? result.filter(d => d.status === 'ACTIVE') : []);
    } catch {
      setDefs([]);
    }
  }, [wsId]);

  useEffect(() => { fetchDefs(); }, [fetchDefs]);

  useEffect(() => {
    window.addEventListener('nx-custom-objects-changed', fetchDefs);
    return () => window.removeEventListener('nx-custom-objects-changed', fetchDefs);
  }, [fetchDefs]);

  return defs;
}

const SidebarSection = ({ label, children, collapsed }) => (
  <div className="nx-sb-section">
    {!collapsed && label && <span className="nx-sb-section-label">{label}</span>}
    <div className="nx-sb-section-items">{children}</div>
  </div>
);

const SidebarItem = ({ item, active, collapsed, onClick }) => (
  <button className={'nx-sb-item' + (active ? ' is-active' : '')} onClick={onClick} title={collapsed ? item.label : undefined}>
    {active && <span className="nx-sb-active-bar" />}
    <NexusIcon name={item.icon} size={16} />
    {!collapsed && <span className="nx-sb-label">{item.label}</span>}
    {!collapsed && item.badge != null && <span className={'nx-sb-badge' + (active ? ' is-active' : '')}>{item.badge}</span>}
  </button>
);

const SavedViewItem = ({ view, icon, active, onApply, onRemove }) => (
  <div className={'nx-sb-item is-saved' + (active ? ' is-active' : '')} onClick={() => onApply(view)}>
    {active && <span className="nx-sb-active-bar" />}
    <NexusIcon name={icon} size={14} />
    <span className="nx-sb-label">{view.name}</span>
    <button className="nx-sb-view-x" onClick={(e) => { e.stopPropagation(); onRemove(view.id); }} title="Delete view">
      <NexusIcon name="X" size={11} />
    </button>
  </div>
);

const ProfileMenu = ({ collapsed }) => {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const [theme, setTheme] = useState(() => (NexusTheme && NexusTheme.get()) || 'light');
  const [sessionUser, setSessionUser] = useState(() => window.__nx_auth?.user || null);
  const ref = useRef();
  const menuRef = useRef();
  const toast = useToast();
  const navigate = useNavigate();
  const { can, orgId, workspaceId, role } = useRbac();
  const { signOut } = globalThis.useAuth();

  useEffect(() => {
    const h = (e) => setSessionUser(e.detail?.user || null);
    window.addEventListener('nx-session-changed', h);
    return () => window.removeEventListener('nx-session-changed', h);
  }, []);

  const profile = sessionUser
    ? { name: sessionUser.name, email: sessionUser.email, avatarTone: sessionUser.avatarTone || '#125AB0' }
    : (window.__nx_settings?.profile || { name: '', email: '', avatarTone: '#125AB0' });

  const email = profile.email || (window.__nx_auth?.user?.email) || '';
  const roleDisplay = ({ 'Super Admin': 'Super Admin', 'Organization Admin': 'Org Admin', 'Workspace Admin': 'WS Admin', 'Workspace User': 'Member' })[role] || role || 'Member';
  const isDark = theme === 'dark' || (theme === 'system' && NexusTheme && NexusTheme.getResolved() === 'dark');

  useEffect(() => {
    if (!NexusTheme) return;
    return NexusTheme.subscribe((mode) => setTheme(mode));
  }, []);

  useEffect(() => {
    if (!open) return;
    const fn = (e) => {
      const inTrigger = ref.current?.contains(e.target);
      const inMenu = menuRef.current?.contains(e.target);
      if (!inTrigger && !inMenu) setOpen(false);
    };
    const kfn = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', fn);
    document.addEventListener('keydown', kfn);
    return () => { document.removeEventListener('mousedown', fn); document.removeEventListener('keydown', kfn); };
  }, [open]);

  const toggleTheme = (e) => {
    e.stopPropagation();
    if (!NexusTheme) return;
    const next = NexusTheme.getResolved() === 'dark' ? 'light' : 'dark';
    NexusTheme.set(next);
    try {
      const raw = localStorage.getItem('nx-settings-v1');
      const s = raw ? JSON.parse(raw) : { profile: {} };
      s.profile = { ...(s.profile || {}), theme: next, darkMode: next === 'dark' };
      localStorage.setItem('nx-settings-v1', JSON.stringify(s));
      window.__nx_settings = s;
    } catch (_) {}
    toast && toast({ tone: 'success', icon: 'Check', title: (next === 'dark' ? 'Dark' : 'Light') + ' Mode' });
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>

      {/* ── Trigger ── */}
      <button
        className="nx-profile-trigger"
        onClick={() => {
          if (!open) {
            const r = ref.current && ref.current.getBoundingClientRect();
            setMenuPos(r ? { bottom: window.innerHeight - r.top + 6, left: r.left, width: Math.max(r.width, 216) } : null);
          }
          setOpen(o => !o);
        }}
      >
        <Avatar name={profile.name} tone={profile.avatarTone} size={32} />
        {!collapsed && (
          <>
            <div className="nx-profile-trigger-meta">
              <span className="nx-profile-trigger-name">{profile.name || 'Account'}</span>
              <span className="nx-profile-trigger-role">{roleDisplay}</span>
            </div>
            <NexusIcon name={open ? 'ChevronDown' : 'ChevronUp'} size={13} style={{ color: 'var(--nx-text-muted)', flexShrink: 0 }} />
          </>
        )}
      </button>

      {/* ── Dropdown — rendered via portal to escape sidebar stacking context ── */}
      {open && createPortal(
        <div
          ref={menuRef}
          className="nx-chip-menu nx-profile-menu"
          style={{
            position: 'fixed',
            top: 'auto',
            bottom: menuPos ? menuPos.bottom : 70,
            left: menuPos ? menuPos.left : 8,
            width: menuPos ? menuPos.width : 216,
            zIndex: 9999,
          }}
        >
          {/* Account header */}
          <div className="nx-profile-menu-header">
            <Avatar name={profile.name} tone={profile.avatarTone} size={34} />
            <div style={{ minWidth: 0 }}>
              <div className="nx-profile-menu-name">{profile.name || 'Account'}</div>
              {email && <div className="nx-profile-menu-email">{email}</div>}
            </div>
          </div>

          <div className="nx-chip-menu-divider" />

          {/* Core actions */}
          <button className="nx-chip-menu-item" onClick={() => { navigate('/settings'); setOpen(false); }}>
            <NexusIcon name="User" size={13} /><span>My Profile</span>
          </button>
          <button className="nx-chip-menu-item" onClick={() => { navigate('/settings'); setOpen(false); }}>
            <NexusIcon name="Settings" size={13} /><span>Account Settings</span>
          </button>
          <button className="nx-chip-menu-item" onClick={toggleTheme}>
            <NexusIcon name={isDark ? 'Sun' : 'Moon'} size={13} />
            <span>{isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}</span>
          </button>

          {/* Admin console links */}
          {(can('platform.view') || can('org.view') || (can('workspace.settings') && workspaceId)) && (
            <>
              <div className="nx-chip-menu-divider" />
              {can('platform.view') && (
                <button className="nx-chip-menu-item" onClick={() => { navigate('/platform'); setOpen(false); }}>
                  <NexusIcon name="Shield" size={13} /><span>Control Center</span>
                </button>
              )}
              {can('org.view') && orgId && (
                <button className="nx-chip-menu-item" onClick={() => { navigate('/organization/' + orgId, { state: { from: 'crm' } }); setOpen(false); }}>
                  <NexusIcon name="Building2" size={13} /><span>Organization Console</span>
                </button>
              )}
              {can('workspace.settings') && workspaceId && (
                <button className="nx-chip-menu-item" onClick={() => { navigate('/workspaces/' + workspaceId); setOpen(false); }}>
                  <NexusIcon name="LayoutGrid" size={13} /><span>Workspace Admin</span>
                </button>
              )}
            </>
          )}

          <div className="nx-chip-menu-divider" />
          <button className="nx-chip-menu-item nx-profile-signout" onClick={() => {
            setOpen(false);
            signOut();
            toast && toast({ tone: 'success', icon: 'ArrowRight', title: 'Signed Out', desc: 'See you soon.' });
          }}>
            <NexusIcon name="LogOut" size={13} /><span>Sign Out</span>
          </button>
        </div>,
        document.body
      )}
    </div>
  );
};

export const Sidebar = ({ active, onNavigate, collapsed, onToggle, savedViews = [], activeViewId, onApplyView, onRemoveView, planFeatureKeys = [] }) => {
  /* C-02: single runtime source — useWorkspaceModules() (live, from WorkspaceRuntimeContext).
     Empty array while runtime loads → permissive fallback (all items visible during load).
     Replaces stale session enabledModules from RbacContext. */
  const runtimeModules = useWorkspaceModules();
  const store = useStore();
  const branding = store.branding;

  /* CO-011c: dynamic custom objects in sidebar */
  const workspace = useWorkspace();
  const activeObjects = useActiveCustomObjects(workspace?.id);
  const [coExpanded, setCoExpanded] = useState(false);
  const visibleCo  = coExpanded ? activeObjects : activeObjects.slice(0, CO_SIDEBAR_MAX);
  const coOverflow = activeObjects.length - CO_SIDEBAR_MAX;

  /* C-03: unified module + plan gate applied to every sidebar section.
     moduleOk: item's backend name must be in runtimeModules (or runtimeModules empty = loading).
     featureOk: org plan must include the item's feature key (or planFeatureKeys empty = loading).
     If MODULE_ID_TO_BACKEND has no entry for an id, the item passes (no gate for unmapped items). */
  const gateItem = (item) => {
    const backendName = MODULE_ID_TO_BACKEND[item.id];
    const moduleOk = !runtimeModules.length || !backendName || runtimeModules.includes(backendName);
    const featureOk = !planFeatureKeys.length || hasFeature(planFeatureKeys, item.featureKey);
    return moduleOk && featureOk;
  };

  const visibleModules    = SIDEBAR_WORKSPACE.filter(gateItem);
  const visibleSalesModules  = SIDEBAR_SALES.filter(gateItem);
  const visibleEngageModules = SIDEBAR_ENGAGE.filter(gateItem);
  const visibleAiModules     = SIDEBAR_AI.filter(gateItem);
  const visibleInsights5     = SIDEBAR_INSIGHTS5.filter(gateItem);
  const visiblePlatform5     = SIDEBAR_PLATFORM5.filter(gateItem);

  return (
    <aside className={'nx-sidebar' + (collapsed ? ' is-collapsed' : '')}>
      <button className="nx-sidebar-brand" onClick={() => onNavigate('home')} title="Go to Dashboard">
        {branding?.logoUrl
          ? <img className="nx-brand-mark nx-brand-logo-img" src={branding.logoUrl} alt={branding.brandName || 'Logo'} style={{ height: 20, objectFit: 'contain' }} />
          : <img className="nx-brand-mark" src="/ursa-logo.svg" alt="URSA" style={{ height: 20, width: 20, objectFit: 'contain', borderRadius: 4 }} />}
        {!collapsed && <span className="nx-brand-word">{branding?.brandName || 'URSA'}</span>}
      </button>
      <nav className="nx-sidebar-nav">
        <SidebarSection collapsed={collapsed}>
          <SidebarItem item={{ id: 'home', label: 'Home', icon: 'Home' }} active={active === 'home'} collapsed={collapsed} onClick={() => onNavigate('home')} />
        </SidebarSection>
        <SidebarSection label="CRM" collapsed={collapsed}>
          {visibleModules.map(item => (
            <SidebarItem key={item.id} item={item} active={active === item.id} collapsed={collapsed} onClick={() => onNavigate(item.id)} />
          ))}
        </SidebarSection>
        {activeObjects.length > 0 && (
          <SidebarSection label="Objects" collapsed={collapsed}>
            {visibleCo.map(obj => (
              <SidebarItem
                key={'co:' + obj.apiName}
                item={{ id: 'co:' + obj.apiName, label: obj.pluralName, icon: obj.icon || 'Database' }}
                active={active === 'co:' + obj.apiName}
                collapsed={collapsed}
                onClick={() => onNavigate('co:' + obj.apiName)}
              />
            ))}
            {!coExpanded && coOverflow > 0 && (
              <button
                className="nx-sb-item"
                style={{ color: 'var(--nx-text-muted)', fontSize: 12 }}
                onClick={() => setCoExpanded(true)}
                title={`Show ${coOverflow} more`}
              >
                <NexusIcon name="MoreHorizontal" size={14} />
                {!collapsed && <span className="nx-sb-label">+{coOverflow} more</span>}
              </button>
            )}
          </SidebarSection>
        )}
        <SidebarSection label="Sales" collapsed={collapsed}>
          {visibleSalesModules.map(item => (
            <SidebarItem key={item.id} item={item} active={active === item.id} collapsed={collapsed} onClick={() => onNavigate(item.id)} />
          ))}
        </SidebarSection>
        <SidebarSection label="Engage" collapsed={collapsed}>
          {visibleEngageModules.map(item => (
            <SidebarItem key={item.id} item={item} active={active === item.id} collapsed={collapsed} onClick={() => onNavigate(item.id)} />
          ))}
        </SidebarSection>
        {visibleAiModules.length > 0 && (
          <SidebarSection label="AI" collapsed={collapsed}>
            {visibleAiModules.map(item => (
              <SidebarItem key={item.id} item={item} active={active === item.id} collapsed={collapsed} onClick={() => onNavigate(item.id)} />
            ))}
          </SidebarSection>
        )}
        {visibleInsights5.length > 0 && (
          <SidebarSection label="Insights" collapsed={collapsed}>
            {visibleInsights5.map(item => (
              <SidebarItem key={item.id} item={item} active={active === item.id} collapsed={collapsed} onClick={() => onNavigate(item.id)} />
            ))}
          </SidebarSection>
        )}
        {visiblePlatform5.length > 0 && (
          <SidebarSection label="Platform" collapsed={collapsed}>
            {visiblePlatform5.map(item => (
              <SidebarItem key={item.id} item={item} active={active === item.id} collapsed={collapsed} onClick={() => onNavigate(item.id)} />
            ))}
          </SidebarSection>
        )}
        {!collapsed && savedViews.length > 0 && (
          <SidebarSection label="Saved Views">
            {savedViews.map(v => {
              const moduleIcon = { people: 'User', companies: 'Building', deals: 'Target', tasks: 'CheckSquare' }[v.module] || 'Star';
              return <SavedViewItem key={v.id} view={v} icon={moduleIcon} active={activeViewId === v.id} onApply={onApplyView} onRemove={onRemoveView} />;
            })}
          </SidebarSection>
        )}
      </nav>
      <div className="nx-sidebar-foot">
        <ProfileMenu collapsed={collapsed} />
        <button className="nx-collapse-btn" onClick={onToggle} title={collapsed ? 'Expand' : 'Collapse'}>
          <NexusIcon name={collapsed ? 'ChevronRight' : 'ChevronLeft'} size={14} />
        </button>
      </div>
    </aside>
  );
};

/* ═══════════════════════════ Topbar ═══════════════════════════ */
const NotifPopover = ({ anchorRef, onClose }) => {
  const popRef = useRef(null);
  const [style, setStyle] = useState({});

  // Position the panel flush below the anchor, right-aligned, within viewport
  useEffect(() => {
    const position = () => {
      const anchor = anchorRef?.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const GAP = 8;
      const W   = Math.min(400, window.innerWidth - 16);
      const top = rect.bottom + GAP;
      // Prefer right-aligned to the anchor; flip left if it would overflow
      let right = window.innerWidth - rect.right;
      if (rect.right - W < 8) right = Math.max(8, right);
      setStyle({ top, right, width: W, maxHeight: window.innerHeight - top - 12 });
    };
    position();
    window.addEventListener('resize', position);
    return () => window.removeEventListener('resize', position);
  }, [anchorRef]);

  // ESC closes the panel
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const NotificationCenter = window.NotificationCenter;
  const content = NotificationCenter
    ? <NotificationCenter onClose={onClose} />
    : (
      <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--nx-text-muted)', fontSize: 13 }}>
        <NexusIcon name="Bell" size={20} />
        <p style={{ marginTop: 8, margin: '8px 0 0' }}>No new notifications</p>
      </div>
    );

  return (
    <div
      ref={popRef}
      className="nx-notif-panel"
      style={{
        position: 'fixed',
        zIndex: 1080,
        display: 'flex',
        flexDirection: 'column',
        ...style,
      }}
    >
      {content}
    </div>
  );
};

export const Topbar = ({ title, breadcrumbs = [], onSearch, onCmd, onCreate, onNavigate, onMenuToggle, workspaceSwitcher }) => {
  const [createOpen, setCreateOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const createRef = useRef();
  const notifRef = useRef();

  useEffect(() => {
    const onDoc = (e) => {
      if (createRef.current && !createRef.current.contains(e.target)) setCreateOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  return (
    <header className="nx-topbar">
      <div className="nx-topbar-left">
        <button className="nx-mobile-menu-btn nx-iconbtn" onClick={onMenuToggle} title="Menu">
          <NexusIcon name="Menu" size={18} />
        </button>
        {workspaceSwitcher && <div className="nx-topbar-wsswitch">{workspaceSwitcher}</div>}
        <div className="nx-topbar-context">
          <h1 className="nx-topbar-title">{title}</h1>
          {breadcrumbs.length > 0 && (
            <div className="nx-breadcrumb">
              {breadcrumbs.map((b, i) => {
                const isLast = i === breadcrumbs.length - 1;
                const label = typeof b === 'string' ? b : b.label;
                const onClick = typeof b === 'object' && b.onClick ? b.onClick : null;
                return (
                  <React.Fragment key={i}>
                    {i > 0 && <NexusIcon name="ChevronRight" size={10} />}
                    {(!isLast && onClick)
                      ? <button className="nx-breadcrumb-btn" onClick={onClick}>{label}</button>
                      : <span className={isLast ? 'is-current' : ''}>{label}</span>
                    }
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <button className="nx-topbar-search" onClick={onCmd}>
        <NexusIcon name="Search" size={14} />
        <span>Search people, companies, tasks and more…</span>
        <span className="nx-topbar-search-kbd"><Kbd>⌘</Kbd><Kbd>K</Kbd></span>
      </button>

      <div className="nx-topbar-right">
        <span ref={notifRef} style={{ position: 'relative' }}>
          {(() => { const Bell = window.NotificationBell; return Bell
            ? <Bell onClick={() => setNotifOpen(o => !o)} />
            : <IconBtn icon="Bell" title="Notifications" onClick={() => setNotifOpen(o => !o)} active={notifOpen} />; })()}
          {notifOpen && <NotifPopover anchorRef={notifRef} onClose={() => setNotifOpen(false)} />}
        </span>
        <span ref={createRef} style={{ position: 'relative' }}>
          <Btn variant="primary" icon="Plus" iconRight="ChevronDown" onClick={() => setCreateOpen(o => !o)} active={createOpen}>New</Btn>
          {createOpen && (
            <div className="nx-chip-menu nx-create-menu" style={{ right: 0, left: 'auto', top: 36 }}>
              <button className="nx-chip-menu-item" onClick={() => { onCreate('people'); setCreateOpen(false); }}><NexusIcon name="User" size={12} /><span>New Person</span><Kbd>P</Kbd></button>
              <button className="nx-chip-menu-item" onClick={() => { onCreate('companies'); setCreateOpen(false); }}><NexusIcon name="Building" size={12} /><span>New Company</span><Kbd>C</Kbd></button>
              <button className="nx-chip-menu-item" onClick={() => { onCreate('deals'); setCreateOpen(false); }}><NexusIcon name="Target" size={12} /><span>New Deal</span><Kbd>D</Kbd></button>
              <button className="nx-chip-menu-item" onClick={() => { onCreate('tasks'); setCreateOpen(false); }}><NexusIcon name="CheckSquare" size={12} /><span>New Task</span><Kbd>T</Kbd></button>
              <button className="nx-chip-menu-item" onClick={() => { onCreate('notes'); setCreateOpen(false); }}><NexusIcon name="StickyNote" size={12} /><span>New Note</span><Kbd>N</Kbd></button>
            </div>
          )}
        </span>
      </div>
    </header>
  );
};

/* ═══════════════════════════ ModuleHeader ═══════════════════════════ */
export const ModuleHeader = ({ title, count, tabs, activeTab, onTab, actions, filters }) => (
  <div className="nx-module-header">
    <div className="nx-module-title-row">
      <div className="nx-module-title-left">
        <h2 className="nx-module-title">{title}</h2>
        {count != null && <span className="nx-module-count">{count.toLocaleString()}</span>}
      </div>
      <div className="nx-module-actions">{actions}</div>
    </div>
    {tabs && (
      <div className="nx-module-tabs-bar">
        <div className="nx-tabs" role="tablist">
          {tabs.map(t => (
            <button type="button" key={t.id} role="tab" aria-selected={activeTab === t.id} className={'nx-tab' + (activeTab === t.id ? ' is-active' : '')} onClick={() => onTab?.(t.id)}>
              {t.icon && <NexusIcon name={t.icon} size={14} />}
              <span>{t.label}</span>
              {t.count != null && <span className="nx-tab-count">{t.count}</span>}
            </button>
          ))}
        </div>
      </div>
    )}
    {filters && <div className="nx-toolbar-filters">{filters}</div>}
  </div>
);

/* ═══════════════════════════ Layout shells ═══════════════════════════ */
function setSidebarCSSVar(collapsed) {
  document.documentElement.style.setProperty(
    '--nx-sidebar-w',
    collapsed ? 'var(--nx-sidebar-collapsed)' : 'var(--nx-sidebar-expanded)'
  );
}

export const AppLayout = ({
  active, onNavigate, topbarTitle, topbarBreadcrumbs,
  onCmd, onCreate,
  savedViews = [], activeViewId, onApplyView, onRemoveView,
  workspaceSwitcher,
  planFeatureKeys = [],
  children, portals,
}) => {
  const [collapsed, setCollapsed] = useState(() => {
    if (window.innerWidth < 1024) return true;
    try { return localStorage.getItem('nx-nav-collapsed') === '1'; } catch (_) { return false; }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const autoCollapsedRef = useRef(false);

  useEffect(() => { setSidebarCSSVar(collapsed); }, [collapsed]);

  useEffect(() => {
    if (active === 'settings') {
      setCollapsed(c => { if (!c) { autoCollapsedRef.current = true; return true; } return c; });
    } else if (autoCollapsedRef.current) {
      autoCollapsedRef.current = false;
      if (window.innerWidth >= 1024) setCollapsed(false);
    }
  }, [active]);

  const toggle = () => {
    setCollapsed(c => {
      const next = !c;
      try { localStorage.setItem('nx-nav-collapsed', next ? '1' : '0'); } catch (_) {}
      setSidebarCSSVar(next);
      return next;
    });
  };

  const handleMenuToggle = () => {
    if (window.innerWidth < 768) setMobileOpen(o => !o);
    else toggle();
  };

  const sidebarCollapsed = window.innerWidth < 768 ? !mobileOpen : collapsed;

  return (
    <LayoutCtx.Provider value={{ collapsed, setCollapsed }}>
      <div className="nx-app" data-screen-label="URSA">
        {mobileOpen && (
          <div className="nx-sidebar-overlay" onClick={() => setMobileOpen(false)} aria-hidden="true" />
        )}
        <Sidebar
          active={active}
          onNavigate={v => { onNavigate(v); setMobileOpen(false); }}
          collapsed={sidebarCollapsed}
          onToggle={toggle}
          savedViews={savedViews}
          activeViewId={activeViewId}
          onApplyView={onApplyView}
          onRemoveView={onRemoveView}
          planFeatureKeys={planFeatureKeys}
        />
        <div className={'nx-main' + (collapsed ? ' is-collapsed' : '')}>
          <Topbar
            title={topbarTitle}
            breadcrumbs={topbarBreadcrumbs}
            onCmd={onCmd}
            onCreate={onCreate}
            onNavigate={onNavigate}
            onMenuToggle={handleMenuToggle}
            workspaceSwitcher={workspaceSwitcher}
          />
          <div className="nx-page-scroll">{children}</div>
        </div>
        {portals}
      </div>
    </LayoutCtx.Provider>
  );
};

export const SettingsWorkspaceLayout = ({ nav, children }) => (
  <div className="nx-view nx-settings">
    <div className="nx-settings-side">{nav}</div>
    <div className="nx-settings-main">{children}</div>
  </div>
);

export const AuthLayout = ({ children }) => (
  <div className="nx-auth">
    {children}
    <div className="nx-auth-bg" aria-hidden="true">
      <span /><span /><span />
    </div>
  </div>
);
